<!--
  The fields of a job site. Shared by the quality SiteForm and the admin Sites
  screen so the two logins edit the same thing the same way. Bindable: the
  parent owns the object and the Save button.
-->
<script lang="ts">
  import type { SaveSiteRequest } from '../../lib/types';
  import { acceptPlaceholder } from '../../lib/fields/accept-placeholder';
  import { session } from '../../lib/store';

  interface Props {
    site: SaveSiteRequest;
    fieldErrors?: Record<string, string>;
  }
  let { site = $bindable(), fieldErrors = {} }: Props = $props();

  /**
   * Whether a job bills for its concrete is a BILLING decision, not a fact
   * observed on site — the app's whole architecture is "quality records the
   * facts, admin supplies the prices" — and it compounds because
   * `bills_concrete = 0` deliberately suppresses the unpriced warning, so a
   * mis-toggle from the quality login yields a quietly smaller revenue
   * figure with the one mechanism that would reveal it switched off by the
   * same flag. This component is reached from both an admin session
   * (AdminSites.svelte) and a quality one (quality/SiteForm.svelte). The
   * route itself refuses this field from a non-admin session (sites.ts) —
   * that is the actual access control. Gating the CONTROL here too is just
   * courtesy: it keeps a quality login from seeing a checkbox it cannot
   * actually move, without needing a round trip to find that out.
   */
  const isAdmin = $derived($session?.area === 'admin');
</script>
<!-- → in an empty field takes the grey example as the value (see lib/fields/accept-placeholder.ts). -->
<div use:acceptPlaceholder>

<label class="field">
  <span class="label">Site name</span>
  <input type="text" bind:value={site.name} placeholder="Kingsway" />
  {#if fieldErrors.name}<span class="field__flag">{fieldErrors.name}</span>{/if}
</label>
<div class="grid2">
  <label class="field">
    <span class="label">Street</span>
    <input type="text" bind:value={site.street} placeholder="3777 Kingsway" />
  </label>
  <label class="field">
    <span class="label">City</span>
    <input type="text" bind:value={site.city} placeholder="Burnaby" />
  </label>
  <label class="field">
    <span class="label">Province</span>
    <input type="text" bind:value={site.province} placeholder="BC" />
  </label>
  <label class="field">
    <span class="label">Postal code</span>
    <input type="text" bind:value={site.postal_code} placeholder="V5H 3Z7" />
  </label>
  <label class="field">
    <span class="label">Start date</span>
    <input type="date" bind:value={site.start_date} />
  </label>
  <label class="field">
    <span class="label">End date</span>
    <input type="date" bind:value={site.end_date} />
  </label>
</div>
<label class="field">
  <span class="label">Notes</span>
  <textarea rows="2" bind:value={site.notes}></textarea>
</label>
{#if isAdmin}
<label class="field field--check">
  <input type="checkbox" bind:checked={site.bills_concrete} />
  <span>
    <span class="label">This job bills for the concrete we pour</span>
    <span class="field__hint">Turn this off when the client supplies the material — pours are still recorded here, but they earn nothing.</span>
  </span>
</label>
{:else}
<!--
  Billing decision, not a fact observed on site (I4) — read-only here, same
  value shown either way: hiding the INFORMATION is worse than hiding the
  CONTROL, even though the control itself is admin-only.
-->
<div class="field field--check field--readonly">
  <input type="checkbox" checked={site.bills_concrete} disabled aria-readonly="true" />
  <span>
    <span class="label">This job bills for the concrete we pour</span>
    <span class="field__hint">A billing decision, not a site fact — only an admin session can change it.</span>
  </span>
</div>
{/if}
</div>

<style>
  .field--check {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.6rem;
  }

  .field--check input {
    margin-top: 0.2rem;
    flex: 0 0 auto;
    width: 1.2rem;
    height: 1.2rem;
  }

  .field--readonly {
    opacity: 0.75;
  }
</style>
